import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useGuestGroup, useTaxes} from 'hooks';
import {FORM_NAMES} from './constants';
import {getFields, getLabels} from './utils';
import CounterStepper from '../common/CounterStepper';
import ModalSheet from '../common/ModalSheet';
import {
  ButtonConfirm,
  Content,
  ErrorContainer,
  ErrorMessage,
  StyledLoader,
} from './styled';

type FormTypes = {
  [FORM_NAMES.junior]: number;
  [FORM_NAMES.middle]: number;
  [FORM_NAMES.senior]: number;
  [FORM_NAMES.others]: number;
  [FORM_NAMES.numberOfGuests]: number;
};

type WhoComingModalSheetProps = {
  isOpenWhoComingModalSheet: boolean;
  handleCloseModal: () => void;
  yearsAbove?: number;
  yearsUnder?: number;
};

function WhoComingModalSheet({
  isOpenWhoComingModalSheet,
  handleCloseModal,
}: WhoComingModalSheetProps) {
  const {t} = useTranslation();
  const {seasonEntity, isFetching: isTaxesFetching} = useTaxes();
  const [errorMessage, setErrorMessage] = React.useState('');

  const visibleFields = getFields({
    lessThanRule: seasonEntity.lessThanRule,
    betweenRule: seasonEntity.betweenRule,
    moreThanRule: seasonEntity.moreThanRule,
  });

  const labels = getLabels({
    lessThanRule: seasonEntity.lessThanRule,
    betweenRule: seasonEntity.betweenRule,
    moreThanRule: seasonEntity.moreThanRule,
    visibleFields,
    t,
  });

  const {control, reset, handleSubmit} = useForm<FormTypes>();

  const {
    guestGroup,
    updateGuestGroup: patchGuestGroup,
    isLoading: isGuestGroupLoading,
    isFetching: isGuestGroupFetching,
    guestGroupMutation,
  } = useGuestGroup();

  const isLoading = isGuestGroupLoading || isGuestGroupFetching || isTaxesFetching;
  const disabled = isLoading;

  React.useEffect(() => {
    if (guestGroupMutation.error) {
      setErrorMessage(guestGroupMutation.error?.errors[0].message);
    }
  }, [guestGroupMutation.error]);

  React.useEffect(() => {
    if (guestGroupMutation.isSuccess) {
      setErrorMessage('');
      handleCloseModal();
    }
  }, [guestGroupMutation.isSuccess, handleCloseModal]);

  const updateGuestGroup = React.useCallback(
    (formData: FormTypes) => {
      const senior = visibleFields[FORM_NAMES.senior] ? formData[FORM_NAMES.senior] : 0;
      const middle = visibleFields[FORM_NAMES.middle] ? formData[FORM_NAMES.middle] : 0;
      const junior = visibleFields[FORM_NAMES.junior] ? formData[FORM_NAMES.junior] : 0;
      const numberOfGuests = visibleFields[FORM_NAMES.numberOfGuests]
        ? formData[FORM_NAMES.numberOfGuests]
        : 0;

      const guestGroupPayload = {
        seniors: senior,
        adults: middle || numberOfGuests,
        children: junior,
      };

      patchGuestGroup(guestGroupPayload, {
        onSuccess: () => {
          handleCloseModal();
        },
      });
    },
    [handleCloseModal, patchGuestGroup, visibleFields],
  );

  React.useEffect(
    function preload() {
      if (typeof guestGroup?.known_number_of_guests !== 'number') return;
      const isNotDefinedAnyGroup =
        !guestGroup?.children && !guestGroup?.adults && !guestGroup?.seniors;

      if (isNotDefinedAnyGroup) {
        reset({
          [FORM_NAMES.numberOfGuests]: guestGroup?.known_number_of_guests ?? 0,
          [FORM_NAMES.junior]: 0,
          [FORM_NAMES.middle]: guestGroup?.known_number_of_guests ?? 0,
          [FORM_NAMES.senior]: 0,
        });
        return;
      }

      reset({
        [FORM_NAMES.numberOfGuests]: guestGroup?.known_number_of_guests ?? 0,
        [FORM_NAMES.junior]: guestGroup?.children ?? 0,
        [FORM_NAMES.middle]: guestGroup?.adults ?? 0,
        [FORM_NAMES.senior]: guestGroup?.seniors ?? 0,
      });
    },
    [
      guestGroup?.adults,
      guestGroup?.seniors,
      guestGroup?.children,
      guestGroup?.known_number_of_guests,
      reset,
    ],
  );

  return (
    <ModalSheet
      isOpenModalSheet={isOpenWhoComingModalSheet}
      handleCloseModal={handleCloseModal}
      title={t('number_of_guest')}
      closeOnEscape
      withCloseButton
      // width={450}
    >
      <Content isLoading={isLoading}>
        <StyledLoader isLoading={isLoading} loaderWidth={80}>
          {visibleFields[FORM_NAMES.numberOfGuests] && (
            <Controller
              name={FORM_NAMES.numberOfGuests}
              control={control}
              render={({field, fieldState: {error}}) => {
                return (
                  <CounterStepper
                    title={t('number_of_guests')}
                    error={error?.message}
                    disabled={disabled}
                    {...field}
                  />
                );
              }}
            />
          )}
          {visibleFields[FORM_NAMES.junior] && (
            <Controller
              name={FORM_NAMES.junior}
              control={control}
              render={({field, fieldState: {error}}) => {
                return (
                  <CounterStepper
                    title={labels.junior}
                    disabled={disabled}
                    error={error?.message}
                    {...field}
                  />
                );
              }}
            />
          )}
          {visibleFields[FORM_NAMES.middle] && (
            <Controller
              name={FORM_NAMES.middle}
              control={control}
              render={({field, fieldState: {error}}) => {
                return (
                  <CounterStepper
                    title={labels.middle}
                    disabled={disabled}
                    error={error?.message}
                    {...field}
                  />
                );
              }}
            />
          )}
          {visibleFields[FORM_NAMES.senior] && (
            <Controller
              name={FORM_NAMES.senior}
              control={control}
              render={({field, fieldState: {error}}) => {
                return (
                  <CounterStepper
                    title={labels.senior}
                    error={error?.message}
                    disabled={disabled}
                    {...field}
                  />
                );
              }}
            />
          )}
          {visibleFields[FORM_NAMES.others] && (
            <Controller
              name={FORM_NAMES.others}
              control={control}
              render={({field, fieldState: {error}}) => {
                return (
                  <CounterStepper
                    title={t('others')}
                    error={error?.message}
                    disabled={disabled}
                    {...field}
                  />
                );
              }}
            />
          )}
          {Boolean(errorMessage) && (
            <ErrorContainer>
              <ErrorMessage>{errorMessage}</ErrorMessage>
            </ErrorContainer>
          )}
          <ButtonConfirm
            primary
            label={t('confirm')}
            disabled={disabled}
            // width={250}
            onClick={handleSubmit(updateGuestGroup)}
            // onClick={handleCloseModal}
          />
        </StyledLoader>
      </Content>
    </ModalSheet>
  );
}
export {WhoComingModalSheet};
